import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  ListItem,
  OrderedList,
  SimpleGrid,
  Stack,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUserContext } from 'context/UserContextProvider';
import { motion } from 'framer-motion';
import useUpdateSettingsMutation from 'hooks/mutations/useUpdateSettingsMutation';
import i18n from 'i18next';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import * as yup from 'yup';

import IconTooltip from '../../components/IconTooltip';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { createDropdownPlainOptions, createDropdownTranslatedStaticOptions } from '../../helpers/general';
import { dropdown } from '../../helpers/makeFormFields';
import useSettingsQuery from '../../hooks/useSettingsQuery';

const PersonalSettingsForm = () => {
  const { t } = useTranslation();
  const settingsQuery = useSettingsQuery();
  const updateSettingsMutation = useUpdateSettingsMutation();
  const userContext = useUserContext();
  const userLanguage = userContext.user?.language;
  const spokenLanguagesByUser = settingsQuery?.data?.spoken_languages;
  const spokenLanguagesByUserUpperCase = spokenLanguagesByUser?.map((language: string) => {
    return language.toUpperCase();
  });

  const userTimezone = settingsQuery.data?.timezone;
  const timezones = [
    { label: 'Iceland GMT+0', value: 'Atlantic/Reykjavik' },
    { label: 'Europe/London GMT+0', value: 'Europe/London' },
    { label: 'Europe/Vienna GMT+1', value: 'Europe/Vienna' },
    { label: 'Europe/Athens GMT+2', value: 'Europe/Athens' },
  ];

  const schema = yup
    .object({
      timezone: yup.string().required(t('user:personal_settings.error_message.timezone')),
      language: yup.string().required(t('user:personal_settings.error_message.language')),
      spoken_languages: yup.mixed(),
    })
    .required();

  const allAvailableLanguages = userContext.config?.languages;
  const allAvailableLanguagesWithDuplicates = Object.values(allAvailableLanguages).reduce(
    (allLanguages, language) => allLanguages.concat(language),
    [],
  );
  const allAvailableLanguagesWithoutDuplicates = new Set(allAvailableLanguagesWithDuplicates);
  const allAvailableLanguagesWithoutDuplicatesArray = Array.from(allAvailableLanguagesWithoutDuplicates);

  const availableLanguagesToSelect = allAvailableLanguagesWithoutDuplicatesArray.filter(
    (item: any) => !spokenLanguagesByUserUpperCase?.includes(item),
  );
  const [availableLanguages, setAvailableLanguages] = useState(availableLanguagesToSelect);
  const [spokenLanguages, setSpokenLanguages] = useState(spokenLanguagesByUserUpperCase!);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      timezone: userTimezone ? userTimezone : '',
      language: userLanguage ? userLanguage : '',
      spoken_languages: spokenLanguages,
    },
  });
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = form;
  const selectedTimezone = watch('timezone');
  const selectedLanguage = watch('language');

  useEffect(() => {
    setValue('timezone', `${selectedTimezone}`);
    setValue('language', `${selectedLanguage}`);
    setValue('spoken_languages', spokenLanguages as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimezone, selectedLanguage, spokenLanguages]);

  useEffect(() => {
    setValue('timezone', `${userTimezone}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTimezone]);

  useEffect(() => {
    if (settingsQuery?.data) {
      setAvailableLanguages(availableLanguagesToSelect);
      setSpokenLanguages(spokenLanguagesByUserUpperCase!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spokenLanguagesByUser]);

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    // @ts-expect-error
    updateSettingsMutation.mutate(data);
  };

  function addSpokenLanguage(index: number) {
    //get selected language and remove it from availableLanguages
    const availableLanguagesCopy = [...availableLanguages];
    availableLanguagesCopy.splice(index, 1);
    setAvailableLanguages(availableLanguagesCopy);

    //add selected language to spokenLanguages
    const updatedSpokenLanguages = [...(spokenLanguages ?? [])];
    const selectedLanguage = availableLanguages.splice(index, 1);
    updatedSpokenLanguages.push(selectedLanguage[0]);
    setSpokenLanguages(updatedSpokenLanguages);
  }

  function removeSpokenLanguage(index: number) {
    //deletes selected spoken language from spokenLanguages array
    //removes deleted spoken language from spokenLanguages array
    const spokenLanguagesCopy = [...(spokenLanguages ?? [])];
    const selectedSpokenLanguage = spokenLanguagesCopy.splice(index, 1);
    setSpokenLanguages(spokenLanguagesCopy);

    //adds deleted spoken language to availableLanguages array
    const updatedAvailableLanguages = [...availableLanguages];
    updatedAvailableLanguages.push(selectedSpokenLanguage[0]);
    setAvailableLanguages(updatedAvailableLanguages);
  }

  function changeOrderUp(index: number) {
    //onClick gives language index - 1
    //get selected language and remove it from spokenLanguages
    const spokenLangCopy = [...(spokenLanguages ?? [])];
    spokenLangCopy.splice(index, 1);
    const selectedSpokenLanguageWithNewIndex = spokenLanguages?.splice(index, 1)[0];

    //inserts selected language in the spokenLanguages array at the right place
    spokenLangCopy.splice(index - 1, 0, selectedSpokenLanguageWithNewIndex as string);
    setSpokenLanguages(spokenLangCopy);
  }

  function changeOrderDown(index: number) {
    //onClick gives language index + 1
    //get selected language and remove it from spokenLanguages
    const spokenLangCopy = [...(spokenLanguages ?? [])];
    spokenLangCopy.splice(index, 1);
    const selectedSpokenLanguageWithNewIndex = spokenLanguages?.splice(index, 1)[0];

    //inserts selected language in the spokenLanguages array at the right place
    spokenLangCopy.splice(index + 1, 0, selectedSpokenLanguageWithNewIndex as string);
    setSpokenLanguages(spokenLangCopy);
  }

  if (settingsQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider {...form}>
      <Stack gap={8} maxW="1000px" p="1" as="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2} alignItems={'flex-end'}>
          {dropdown({
            name: 'language',
            label: t('user:personal_settings.language.label'),
            defaultValue: userLanguage ?? '',
            showAsTooltip: true,
            description: t('user:personal_settings.language.explanation'),
            // @ts-ignore
            control,
            errors,
            options: createDropdownTranslatedStaticOptions({
              records: allAvailableLanguagesWithoutDuplicatesArray,
              translationKey: 'location:languages',
              initialOptions: { value: '', label: i18n.t('common:choose') },
            }),
          })}

          {dropdown({
            name: 'timezone',
            label: t('user:personal_settings.timezone'),
            defaultValue: userTimezone ? userTimezone : '',
            // @ts-ignore
            control,
            errors,
            options: createDropdownPlainOptions({
              records: timezones,
              valueColumn: 'value',
              labelColumn: 'label',
              initialOptions: userTimezone as any,
            }),
          })}
        </Stack>

        <Stack>
          <Heading size="xxs">
            {t('user:personal_settings.spoken_languages.label')} <IconTooltip text={t('user:personal_settings.spoken_languages.explanation')} />
          </Heading>
          <SimpleGrid alignItems={'baseline'} columns={{ lg: 2, base: 1 }} spacing={14}>
            <Box>
              <FormLabel flex={1}>{t('common:available_languages')}</FormLabel>
              <UnorderedList spacing={3} ml={0} mt={0} pt={0}>
                {availableLanguages.map((language, index) => {
                  return (
                    <HStack key={index} spacing={3}>
                      <Flex
                        width={'100%'}
                        pr={6}
                        pl={6}
                        height={'48px'}
                        fontSize={'md'}
                        alignItems={'center'}
                        border={'1px'}
                        borderColor={'gray.200'}
                        borderRadius={'8'}
                        bgColor={'white'}
                      >
                        <Text>{t(`location:languages.${language}`)}</Text>
                      </Flex>
                      <IconButton
                        icon={<AddIcon boxSize={4} />}
                        aria-label={t('common:add')}
                        p={'5'}
                        borderRadius={'6'}
                        fontSize={'xs'}
                        variant={'none'}
                        border={'1px'}
                        borderColor={'green'}
                        color={'green'}
                        size="lg"
                        _hover={{
                          bgColor: 'green.200',
                        }}
                        onClick={() => {
                          addSpokenLanguage(index);
                        }}
                      />
                    </HStack>
                  );
                })}
              </UnorderedList>
            </Box>

            <Box>
              <FormLabel>{t('user:personal_settings.spoken_languages.label')}</FormLabel>
              <OrderedList spacing={3} ml={0}>
                {spokenLanguages?.map((language: string, index: number) => {
                  return (
                    <HStack key={index} spacing={3}>
                      <Flex
                        border={'1px'}
                        borderColor={'gray.200'}
                        borderRadius={'8'}
                        bgColor={'white'}
                        width={'100%'}
                        height={'48px'}
                        pl={10}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        display={'flex'}
                      >
                        <ListItem fontSize={'md'}>{t(`location:languages.${language}`)}</ListItem>
                        <VStack>
                          <Button
                            mb={-3}
                            size="xs"
                            variant="none"
                            _hover={{
                              bgColor: 'gray.100',
                            }}
                            onClick={() => {
                              changeOrderUp(index);
                            }}
                            disabled={index === 0}
                          >
                            <motion.div whileHover={{ scale: 1.5 }} transition={{ duration: 0.2 }}>
                              <Icon as={FiChevronUp} />
                            </motion.div>
                          </Button>

                          <Button
                            size="xs"
                            variant="none"
                            _hover={{
                              bgColor: 'gray.100',
                            }}
                            onClick={() => {
                              changeOrderDown(index);
                            }}
                            disabled={index === spokenLanguages.length - 1}
                          >
                            <motion.div whileHover={{ scale: 1.5 }} transition={{ duration: 0.2 }}>
                              <Icon as={FiChevronDown} />
                            </motion.div>
                          </Button>
                        </VStack>
                      </Flex>
                      <IconButton
                        icon={<DeleteIcon boxSize={4} />}
                        aria-label={t('common:delete')}
                        padding={'5'}
                        borderRadius={'6'}
                        fontSize={'xs'}
                        variant={'none'}
                        border={'1px'}
                        borderColor={'red'}
                        color={'red'}
                        size="lg"
                        _hover={{
                          bgColor: 'red.200',
                        }}
                        onClick={() => {
                          removeSpokenLanguage(index);
                        }}
                      />
                    </HStack>
                  );
                })}
              </OrderedList>
            </Box>
          </SimpleGrid>
        </Stack>

        <Flex justifyContent="flex-end">
          <Button type="submit" variant="primary" disabled={!selectedTimezone || !selectedLanguage || spokenLanguages?.length === 0}>
            {t('common:update')}
          </Button>
        </Flex>
      </Stack>
    </FormProvider>
  );
};
export default PersonalSettingsForm;
